import { ApiError } from '@/api/errors'
import {
	BILLING_ERROR_CODES,
	type CancelSubscriptionResponse,
	type WireCheckoutResponse,
	type WireOrderResponse,
	type WirePlan
} from '@/api/types'
import { PRO_PLAN_FALLBACK, PRO_PLAN_SLUG } from '@/constants/pro'

// Мок биллинга. Включается VITE_AUTH_MOCK=true — тем же ключом, что и мок входа
// (см. authMock.ts): бэк платёжных ручек пока не отдаёт, а проверять флоу нужно уже сейчас.
//
// Отдаёт сырую форму провода (pending/success/fail), а не наши типы: ответ мока проходит
// через тот же перевод статусов в endpoints/billing.ts, что и настоящий, — иначе мок
// проверял бы контракт, которого не существует.

/**
 * Сценарий оплаты: `localStorage.mock_billing = 'declined'` + перезагрузка.
 * - `success` (по умолчанию) — pending → success за ~6 секунд;
 * - `declined` — то же, но заканчивается отказом карты;
 * - `slow` — остаётся в pending до истечения срока: так проверяется ограничение
 *   генераций и шторка ожидания;
 * - `cancel` — человек закрывает виджет крестиком: заказ навсегда остаётся в pending,
 *   но попытки списания не было, и ограничивать генерации нельзя. Различить эти два
 *   pending бэк не может — знает только фронт, от виджета;
 * - `not_found` — поллинг сразу получает 404: проверка того, что фронт останавливается,
 *   а не долбит в ошибку до дедлайна;
 * - `no_invoice` — чекаут отдаёт payload без `invoiceId`. Наблюдать заказ нечем, поэтому
 *   виджет открыться не должен;
 * - `amount_in_kopecks` — checkout отдаёт сумму в копейках. Проверка защитного ассерта:
 *   виджет открыться не должен.
 */
const SCENARIO_KEY = 'mock_billing'

type Scenario = 'success' | 'declined' | 'slow' | 'cancel' | 'not_found' | 'no_invoice' | 'amount_in_kopecks'

/** Заказ переживает перезагрузку страницы — иначе не проверить восстановление. */
const ORDER_KEY = 'mock_billing_order'

/** Сколько мок держит заказ в pending, прежде чем отдать терминальный статус. */
const PROCESSING_MS = 6000

const ORDER_TTL_MS = 15 * 60 * 1000

const LATENCY_MS = 300

function delay(ms: number = LATENCY_MS) {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

function scenario(): Scenario {
	const raw = localStorage.getItem(SCENARIO_KEY)
	const known: Scenario[] = ['success', 'declined', 'slow', 'cancel', 'not_found', 'no_invoice', 'amount_in_kopecks']
	return known.find((item) => item === raw) ?? 'success'
}

interface MockOrder {
	/** Собственный номер заказа бэка. Фронт его не видит — он есть только в ответе чекаута. */
	orderId: string
	/**
	 * Номер, которым заказ опрашивается. Намеренно не равен orderId: иначе мок принимал бы
	 * оба, и поллинг по чужому id прошёл бы незамеченным до продакшена.
	 */
	invoiceId: string
	createdAt: number
	expiresAt: string
	scenario: Scenario
}

function readOrder(): MockOrder | null {
	try {
		const raw = localStorage.getItem(ORDER_KEY)
		return raw ? (JSON.parse(raw) as MockOrder) : null
	} catch {
		return null
	}
}

function writeOrder(order: MockOrder): void {
	localStorage.setItem(ORDER_KEY, JSON.stringify(order))
}

// Счётчик, а не только время: два checkout в одной миллисекунде дали бы один id,
// а теперь каждый заказ обязан быть новым.
let orderCounter = 0

function makeOrderId(): string {
	orderCounter += 1
	return `mock-order-${Date.now().toString(36)}-${orderCounter}`
}

function makeInvoiceId(): string {
	return `mock-invoice-${Date.now().toString(36)}-${orderCounter}`
}

/**
 * Сценарий тарифов: `localStorage.mock_plans = 'empty'` + перезагрузка.
 * - `default` — массив из бесплатного и Pro: так проверяется правило выбора тарифа;
 * - `empty` — пустой массив: экраны обязаны уйти на PRO_PLAN_FALLBACK;
 * - `no_slug_match` — Pro с другим слагом: выбор должен состояться по цене;
 * - `no_price` — Pro без цены: тариф обязан быть отброшен целиком, экран уйдёт на фолбэк.
 *
 * Сценария «цена в рублях вместо копеек» здесь нет намеренно: на стороне тарифов такая
 * подмена неотличима от честной цены в 3,99 ₽. Её ловит ассерт суммы в usePaymentFlow —
 * см. сценарий mock_billing = 'amount_in_kopecks'.
 */
const PLANS_SCENARIO_KEY = 'mock_plans'

type PlansScenario = 'default' | 'empty' | 'no_slug_match' | 'no_price'

function plansScenario(): PlansScenario {
	const raw = localStorage.getItem(PLANS_SCENARIO_KEY)
	const known: PlansScenario[] = ['default', 'empty', 'no_slug_match', 'no_price']
	return known.find((item) => item === raw) ?? 'default'
}

const MOCK_FREE_PLAN: WirePlan = {
	name: 'Бесплатный',
	slug: 'free',
	price: 0,
	days_period: 30,
	currency: 'RUB',
	limits: { text: 10, image: 0, song: 0 }
}

const MOCK_PRO_PLAN: WirePlan = {
	name: PRO_PLAN_FALLBACK.name,
	slug: PRO_PLAN_SLUG,
	price: PRO_PLAN_FALLBACK.priceMinor,
	days_period: PRO_PLAN_FALLBACK.daysPeriod,
	currency: PRO_PLAN_FALLBACK.currency,
	limits: { ...PRO_PLAN_FALLBACK.limits }
}

export async function mockGetPlans(): Promise<WirePlan[]> {
	await delay()
	const current = plansScenario()
	const plans = PLANS_BY_SCENARIO[current]
	console.warn(
		`💳 [MOCK] GET /billing/plans (сценарий «${current}»). Другие: localStorage.${PLANS_SCENARIO_KEY} = 'empty' | 'no_slug_match' | 'no_price'`,
		plans
	)
	return plans
}

const PLANS_BY_SCENARIO: Record<PlansScenario, WirePlan[]> = {
	default: [MOCK_FREE_PLAN, MOCK_PRO_PLAN],
	empty: [],
	no_slug_match: [MOCK_FREE_PLAN, { ...MOCK_PRO_PLAN, slug: 'pro_monthly_v2' }],
	no_price: [MOCK_FREE_PLAN, { ...MOCK_PRO_PLAN, price: undefined }]
}

export async function mockPostCheckout(planId: string): Promise<WireCheckoutResponse> {
	await delay()
	const current = scenario()
	// Идемпотентности нет: каждый checkout создаёт новый заказ, а прошлый протухает сам.
	// Мок повторяет это буквально — прошлый заказ он забывает, и запрос его статуса
	// отдаёт 404. На бэке протухший заказ проживёт до конца срока, но фронту это
	// безразлично: и 404, и вечный pending он одинаково бросает.
	const order: MockOrder = {
		orderId: makeOrderId(),
		invoiceId: makeInvoiceId(),
		createdAt: Date.now(),
		expiresAt: new Date(Date.now() + ORDER_TTL_MS).toISOString(),
		scenario: current
	}
	writeOrder(order)

	// Форма ответа — та, что бэк отдаёт на dev: телом приходит сам payload виджета,
	// без конверта с order_id, status и expires_at. Старую форму (payload внутри widget)
	// разбирает тот же toCheckout, она проверена в спеке роутинга.
	const response: WireCheckoutResponse = {
		// Ключ терминала Intent API: именно этим именем его отдаёт бэк, и его ждёт widget.start.
		publicTerminalId: 'pt_mock000000000000000000000000',
		// В сценарии amount_in_kopecks сумма намеренно испорчена: виджет открыться не должен.
		amount: current === 'amount_in_kopecks' ? PRO_PLAN_FALLBACK.priceMinor : PRO_PLAN_FALLBACK.priceMajor,
		currency: 'RUB',
		description: 'PomogAI Pro, 30 дней',
		accountId: 'mock@example.com',
		// Ключ наблюдения за заказом. В сценарии no_invoice его нет: фронт обязан
		// отказаться открывать виджет, а не молча пойти опрашивать что-нибудь другое.
		invoiceId: current === 'no_invoice' ? undefined : order.invoiceId,
		skin: 'Modern',
		paymentSchema: 'Single',
		autoClose: 3,
		tokenize: true,
		// Собственное поле payload, а не конверт ответа: на нём спотыкается наивный unwrap.
		data: { intention: 'subscribe', user_id: 'mock-user' }
	}
	console.warn(
		`💳 [MOCK] POST /billing/checkout plan_id=«${planId}» (сценарий «${current}»). Другие: localStorage.${SCENARIO_KEY} = 'declined' | 'slow' | 'not_found' | 'no_invoice' | 'amount_in_kopecks'`,
		response
	)
	return response
}

/**
 * Статус заказа. Аргумент — invoiceId из платёжного payload, а не order_id ответа чекаута:
 * бэк ключует ручку именно им. Мок сверяет только с invoiceId — поллинг по order_id обязан
 * получить 404, иначе регресс дожил бы до продакшена.
 */
export async function mockGetOrder(invoiceId: string): Promise<WireOrderResponse> {
	await delay(150)
	const order = readOrder()
	if (!order || order.invoiceId !== invoiceId || order.scenario === 'not_found') {
		throw new ApiError(BILLING_ERROR_CODES.ORDER_NOT_FOUND, 'Заказ не найден', { status: 404 })
	}

	const elapsed = Date.now() - order.createdAt
	// Истёкший срок отдельного статуса не имеет: заказ так и висит в pending, а бросает
	// поллинг сам фронт — по своему дедлайну. Именно так ведёт себя контракт.
	const stillPending = order.scenario === 'cancel' || order.scenario === 'slow' || elapsed < PROCESSING_MS

	let status: WireOrderResponse['status'] = 'pending'
	let failureReason: WireOrderResponse['failure_reason'] = null

	if (stillPending) {
		status = 'pending'
	} else if (order.scenario === 'declined') {
		status = 'fail'
		failureReason = 'card_declined'
	} else {
		status = 'success'
		// Успешная оплата поднимает тариф: /usage после этого должен отдавать pro-срез.
		localStorage.setItem('mock_plan', 'pro')
	}

	return { order_id: order.orderId, status, failure_reason: failureReason, expires_at: order.expiresAt }
}

/**
 * Отмена незавершённой оплаты. Мок забывает заказ — так же, как его гасит бэк: после
 * abort статус этого заказа больше не спросить (mockGetOrder ответит 404).
 */
export async function mockAbortPayment(): Promise<void> {
	await delay()
	resetMockOrder()
	console.warn('💳 [MOCK] POST /billing/abort-payment — незавершённый заказ погашен')
}

export async function mockCancelSubscription(): Promise<CancelSubscriptionResponse> {
	await delay()
	console.warn('💳 [MOCK] POST /billing/subscription/cancel — автопродление отключено')
	return { status: 'cancelled', auto_renew: false, current_period_end: null }
}

/** Сброс мок-заказа: `billingMockReset()` из консоли. Нужен, чтобы не чистить ключи руками. */
export function resetMockOrder(): void {
	localStorage.removeItem(ORDER_KEY)
}
